import { useTranslation } from 'react-i18next'
import { formatMoney } from '../utils/money'
import { useCompactTable } from '@/lib/tableDensity'
import { useRowWarnings } from '@/lib/rowWarnings'
import { ValidationWarningsIcon } from '@/components/ValidationWarnings'
import { formatNumber, formatDate, formatDateTime } from '../utils/format'

const DASH = '—'

function isEmpty(value: unknown) {
  return value === null || value === undefined || value === ''
}

/**
 * Identity cell every list starts with: the row's name, an optional second
 * line, and the object id beneath. Shared so every list reads the same. A
 * compact list leaves the id out. A row with validation warnings gets a
 * warning icon after the name that lists them on hover.
 */
export function NameCell({
  name,
  id,
  sub,
}: {
  name?: string | null
  id?: string | null
  sub?: string | null
}) {
  const compact = useCompactTable()
  const warnings = useRowWarnings()
  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5">
        <span className="font-semibold text-(--ad-text)">{isEmpty(name) ? DASH : name}</span>
        <ValidationWarningsIcon warnings={warnings} />
      </span>
      {!isEmpty(sub) && <span className="text-xs text-(--ad-text-muted)">{sub}</span>}
      {!compact && !isEmpty(id) && <span className="font-mono text-[11px] text-(--ad-text-faint)">{id}</span>}
    </div>
  )
}

/** Muted secondary text — the default look for informational table cells. */
export function MutedCell({ value }: { value?: string | number | null }) {
  return (
    <span className="text-xs text-(--ad-text-muted)">
      {isEmpty(value) ? DASH : value}
    </span>
  )
}

/** Right-aligned-ish numeric value with tabular figures. */
export function NumberCell({ value, compact }: { value?: number | null; compact?: boolean }) {
  return (
    <span className="text-xs tabular-nums text-(--ad-text)">
      {value === null || value === undefined
        ? DASH
        : formatNumber(value, compact ? { notation: 'compact' } : {})}
    </span>
  )
}

/** Currency amount as returned by the API. */
export function MoneyCell({
  value,
  fractionDigits = 2,
}: {
  value?: { amount: number; currency: string } | null
  /** Raise this for amounts that run finer than cents, like CPM fees. */
  fractionDigits?: number
}) {
  if (!value) return <MutedCell />
  return (
    <span className="text-xs font-semibold tabular-nums text-(--ad-text)">
      {formatMoney(value.amount, value.currency, fractionDigits)}
    </span>
  )
}

/** ISO timestamp rendered in the user's locale. */
export function DateCell({ value, withTime }: { value?: string | null; withTime?: boolean }) {
  if (isEmpty(value)) return <MutedCell />
  const d = new Date(value as string)
  if (Number.isNaN(d.getTime())) return <MutedCell />
  return (
    <span className="text-xs text-(--ad-text-faint)">
      {withTime ? formatDateTime(d) : formatDate(d)}
    </span>
  )
}

/** Yes / no flag. */
export function BoolCell({ value }: { value?: boolean | null }) {
  const { t } = useTranslation()
  if (value === null || value === undefined) return <MutedCell />
  return (
    <span className="text-xs text-(--ad-text-muted)">
      {value ? t('common.yes') : t('common.no')}
    </span>
  )
}

/** ACTIVE / INACTIVE object state chip, matching the chips used elsewhere. */
export function StatusChip({ state }: { state?: string | null }) {
  if (isEmpty(state)) return <MutedCell />
  return (
    <span className={`chip chip--${state === 'ACTIVE' ? 'running' : 'draft'} text-[11px]`}>
      <span className="dot" />
      {state}
    </span>
  )
}

/** A list of short values, truncated after `max` entries. */
export function ListCell({ values, max = 3 }: { values?: (string | null | undefined)[]; max?: number }) {
  const items = (values ?? []).filter((v): v is string => !isEmpty(v))
  if (!items.length) return <MutedCell />
  const shown = items.slice(0, max)
  return (
    <span className="text-xs text-(--ad-text-muted)" title={items.join(', ')}>
      {shown.join(', ')}
      {items.length > shown.length ? ` +${items.length - shown.length}` : ''}
    </span>
  )
}

/** Monospaced identifier. */
export function IdCell({ value }: { value?: string | null }) {
  if (isEmpty(value)) return <MutedCell />
  return <span className="font-mono text-[11px] text-(--ad-text-faint)">{value}</span>
}
